import React from "react"
import { Box, Typography } from "@mui/material"
import { IconSeeker } from "@ui-seeker/core"

import { dsColorNew } from "@palette"
import AppFormSection, { AppFormGrid } from "@components/@base/FormSection"
import AppPillToggle from "@components/@base/PillToggle"
import { AppNote } from "@components/@base/BulkEditDialog"
import NoDataFound from "@components/DataBlocks/NoDataFound"
import { AppStatusPill } from "@components/DataBlocks/DetailBlocks"
import BrandsField, { fieldLabelSx, fieldNoteSx, helperSx } from "./BrandsField"
import SitesField from "./SitesField"
import { pendingBrandRemoval, setKind } from "./@services/identity"


const KIND_OPTIONS = [
	{ value: 'brand', label: <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><IconSeeker icon="car" sx={{ fontSize: 12 }}/>Brand</Box> },
	{ value: 'broker', label: <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}><IconSeeker icon="globe" sx={{ fontSize: 12 }}/>Broker</Box> },
]

/**
 * The Identity section: Kind · Brands · Sites (CU-869fknfmk). Controlled and stateless — whoever mounts
 * it owns the draft (`value` = `{ kind, provider_ids, site_ids }`, the first brand is the primary; sites have none).
 * `labels` = `labelsFrom(saved, options)`; `options` = the pickers' catalogues; `validation` =
 * `validateDraft(value)`, shown when `showErrors`; `serverError` = `serverFieldError(...)` of a rejected
 * save, shown next to its field until the draft changes.
 */
export default function AppIdentityEditor({ value, onChange, labels, options, saved, validation, serverError = null, showErrors = false, disabled = false }) {
	const removal = pendingBrandRemoval(value, labels)
	const errorOf = (field) => {
		if (showErrors && validation && !validation.ok && validation.field === field) return validation.message
		return serverError?.field === field ? serverError.message : null
	}

	return <AppFormSection
		icon="id-card"
		title="Identity"
		hint="Who “you” are: your brand or broker, its brands and its sites."
		action={!saved && !value.kind ? <AppStatusPill status="not set"/> : null}
	>
		{!value.kind && <NoDataFound
			title="No identity set yet"
			text="Iris doesn't know which brand or site is yours. Choose what your account is to start."
			sx={{ mb: 2, py: 3 }}
		/>}
		<AppFormGrid>
			<Box sx={{ gridColumn: 'span 12' }}>
				<Typography component="span" sx={fieldLabelSx}>Kind <Box component="span" sx={fieldNoteSx}>· required</Box></Typography>
				<AppPillToggle
					size="small"
					aria-label="Kind"
					value={value.kind}
					options={KIND_OPTIONS}
					onChange={kind => !disabled && onChange(setKind(value, kind))}
				/>
				<Typography sx={helperSx}>
					<b>Brand</b> — you rent cars under your own brand or brands. <b>Broker</b> — you sell other companies' cars on your own sites.
				</Typography>
				{!!errorOf('kind') && <Typography role="alert" sx={{ ...helperSx, color: dsColorNew.dangerRed }}>{errorOf('kind')}</Typography>}
				{!!removal && <AppNote severity="warning" sx={{ mt: 1.25 }}>
					As a broker your identity is your sites. <b>Saving removes your {removal.count} brand{removal.count > 1 ? 's' : ''} ({removal.names.join(', ')})</b> — switch back to Brand before saving to keep {removal.count > 1 ? 'them' : 'it'}.
				</AppNote>}
			</Box>

			{value.kind === 'brand' && <BrandsField
				value={value.provider_ids}
				onChange={provider_ids => onChange({ ...value, provider_ids })}
				labels={labels}
				options={options?.providers}
				error={errorOf('brands')}
				disabled={disabled}
			/>}

			{!!value.kind && <SitesField
				value={value.site_ids}
				onChange={site_ids => onChange({ ...value, site_ids })}
				labels={labels}
				options={options?.sites}
				kind={value.kind}
				error={errorOf('sites')}
				disabled={disabled}
			/>}
		</AppFormGrid>
	</AppFormSection>
}

export { default as AppIdentityChip } from "./IdentityChip"
